import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import { OpenAILogo, AmericaLogo, ChinaLogo, WorldLogo } from '@/components/icons';

// Map of entity names to their respective SVG components
const IconComponents: Record<string, React.FC<any>> = {
  'OpenBrain': OpenAILogo,
  'DeepCent': ChinaLogo,
  'Rest of US': AmericaLogo,
  'RestUs': AmericaLogo, // Alternate key for Rest of US
  'Resto de los EE. UU.': AmericaLogo, // es
  'Reste des États-Unis': AmericaLogo, // fr
  'Il resto degli Stati Uniti': AmericaLogo, // it
  'World': WorldLogo,
};

interface PieChartProps {
  data: { OpenBrain: number, DeepCent: number, "Rest of US": number, "China": number };
  title: string;
  category: string;
  className?: string;
  icons?: Record<string, string>; // Legacy props - keep for backward compatibility
  valueFormatter?: (value: number) => React.ReactNode;
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  className,
  icons,
  valueFormatter,
}) => {
  // Refs and state for the center label
  const svgRef = useRef<SVGSVGElement>(null);
  const [centerContent, setCenterContent] = useState<{ text: string; value?: number }>({ text: title });

  // Compute the total value of all slices to later determine percentages.
  const totalValue = useMemo(() => {
    if (!data) return 0;
    const pieData = Object.entries(data)
      .filter(([key]) => key !== 'date' && key !== 'dateLabel')
      .map(([_, value]) => value as number);
    return pieData.reduce((sum, value) => sum + value, 0);
  }, [data]);

  // Chart configuration
  const centerX = 100;
  const centerY = 100;
  const radius = 50;
  const innerRadius = 30;

  // Utility for formatting values.
  const getFormattedValue = (value: number): string => {
    if (!valueFormatter) {
      return value.toString();
    }
    const formatted = valueFormatter(value);
    return typeof formatted === 'string'
      ? formatted
      : typeof formatted === 'number'
      ? formatted.toString()
      : React.isValidElement(formatted)
      ? (formatted.props.children?.toString() || '')
      : '';
  };

  // Create our arc generator (using D3's arc)
  const arcGenerator = useMemo(() => {
    return d3
      .arc<d3.PieArcDatum<{ label: string; value: number }>>()
      .innerRadius(innerRadius)
      .outerRadius(radius);
  }, [innerRadius, radius]);

  // Compute the "static" arcs from the data.
  const staticArcs = useMemo(() => {
    if (!data) return [];
    // Create pie data by filtering out unwanted keys.
    const pieData = Object.entries(data)
      .filter(([key]) => key !== 'date' && key !== 'dateLabel')
      .map(([key, value]) => ({ label: key, value: value as number }));

    // Sort so the largest slice comes first.
    pieData.sort((a, b) => b.value - a.value);
    
    // Use D3's pie generator.
    const pieGenerator = d3
      .pie<{ label: string; value: number }>()
      .value(d => d.value)
      .startAngle(-Math.PI / 2)
      .endAngle((3 * Math.PI) / 2)
      .padAngle(0.02)
      .sort(null);

    const arcs = pieGenerator(pieData);

    // Compute the SVG arc path string and centroid for each slice.
    return arcs.map((arcData, i) => {
      return {
        key: arcData.data.label,
        arcData,
        d: arcGenerator(arcData) as string,
        fill:
          arcData.data.label === 'OpenBrain'
            ? 'var(--accent)'
            : `hsl(0, 0%, ${0 + (i / pieData.length) * 20}%)`,
        centroid: arcGenerator.centroid(arcData) as [number, number],
      };
    });
  }, [data, arcGenerator]);

  // Our animated arcs state – initially we display the static arcs.
  const [animatedArcs, setAnimatedArcs] = useState(staticArcs);

  // Animate transitions when the static (target) arcs change.
  useEffect(() => {
    // Build a map from current animated arcs (by key) used as starting values.
    const currentArcsMap = new Map(animatedArcs.map(arc => [arc.key, arc.arcData]));
    const interpolators = staticArcs.map(arc => {
      // Use existing arc as starting state if it exists.
      const startData = currentArcsMap.get(arc.key) || arc.arcData;
      return {
        key: arc.key,
        interp: d3.interpolate(startData, arc.arcData),
        fill: arc.fill,
      };
    });

    const duration = 400; // Duration in milliseconds.
    const startTime = performance.now();

    function animate() {
      const now = performance.now();
      let t = (now - startTime) / duration;
      if (t > 1) t = 1;

      const newAnimatedArcs = staticArcs.map(arc => {
        const entry = interpolators.find(e => e.key === arc.key);
        if (entry) {
          // Compute the current animated arc data.
          const currentArcData = entry.interp(t);
          const dValue = arcGenerator(currentArcData) as string;
          const centroid = arcGenerator.centroid(currentArcData) as [number, number];
          return {
            key: arc.key,
            arcData: currentArcData,
            d: dValue,
            centroid,
            fill: entry.fill,
          };
        }
        return arc;
      });
      setAnimatedArcs(newAnimatedArcs);
      if (t < 1) {
        requestAnimationFrame(animate);
      }
    }
    requestAnimationFrame(animate);
  }, [staticArcs, arcGenerator]);

  return (
    <div className={`flex ${className} relative items-center justify-center mb-2`}>
      <div>
        <svg
          ref={svgRef}
          viewBox="50 50 100 100"
          className="min-w-[100px]"
          preserveAspectRatio="xMidYMid meet"
        >
          <g transform={`translate(${centerX},${centerY})`}>
            {/* Render each arc as a <path> with interactive events. */}
            {animatedArcs.map(arc => (
              <path
                key={arc.key}
                d={arc.d}
                fill={arc.fill}
                onMouseEnter={() =>
                  setCenterContent({ text: arc.key, value: arc.arcData.data.value })
                }
                onMouseLeave={() => setCenterContent({ text: title })}
              >
                <title>{`${arc.key}`}</title>
              </path>
            ))}
            {/* Render icon groups only if slice is at least 10% of the total. */}
            {animatedArcs.map(arc => {
                // Try to get the icon component for this key
                const IconComponent = IconComponents[arc.key];
                if (!IconComponent) return null;
                
                // Use the static (target) arc data to decide whether to show the icon,
                // so the condition stays consistent during the animation.
                const targetArc = staticArcs.find(a => a.key === arc.key);
                if (!totalValue || !targetArc || targetArc.arcData.data.value < 0.1 * totalValue)
                  return null;
                  
                return (
                  <g
                    onMouseEnter={() => setCenterContent({ text: arc.key, value: arc.arcData.data.value })}
                    onMouseLeave={() => setCenterContent({ text: title })}
                    key={`icon-${arc.key}`}
                    transform={`translate(${arc.centroid[0]},${arc.centroid[1]})`}
                  >
                    <g transform="translate(-7, -7)">
                      <IconComponent 
                        size={14}
                        color="#FFFFFF" 
                        fill="#FFFFFF"
                      />
                    </g>
                  </g>
                );
              })}
          </g>
          <foreignObject x={centerX - 32} y={centerY - 25} width="64" height="50">
            <div className="h-full w-full flex items-center justify-center">
              {centerContent.value && valueFormatter ? (
                <span className="text-[8.5px] font-bold font-sans text-center leading-[1.1]">
                  {centerContent.text} <br/>
                  {valueFormatter(centerContent.value)}
                </span>
              ) : (
                <span className="text-[9px] font-bold font-mono text-center">
                  {centerContent.text}
                </span>
              )}
            </div>
          </foreignObject>
        </svg>
      </div>
    </div>
  );
};

export default PieChart;